import { Templates, Utils } from '@hellohair/types';
import { Box, Divider, ListSubheader, MenuItem, Typography } from '@mui/material';
import { FC, useContext, useState } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../../../../aws/DataProvider';
import { AddRowButton, StepSelect } from '../../../../components/legacy/Questions/_shared/fields';
import { STEP_EDITORS } from '../../../../components/legacy/Questions/_shared/stepEditors';
import { StepRow } from '../../../../components/legacy/Questions/_shared/StepRow';
import {
  STEP_GROUP_COLOR,
  STEP_GROUP_LABEL_KEY,
  STEP_GROUPS,
  stepTypesByGroup,
} from '../../../../components/legacy/Questions/_shared/stepTypes';
import styles from './SurveySingle.styles';

const SurveySingle: FC = () => {
  const {
    formDetailsState: [surveyState, setCurrentSurvey],
  } = useContext(DataContext);

  const { t } = useTranslation();

  const [newType, setNewType] = useState<string>('Welcome');
  /**
   * Tracked by step id, not index, so reordering or deleting a step cannot
   * leave the modal pointed at the wrong one.
   */
  const [editingId, setEditingId] = useState<number | null>(null);

  const steps: Templates.StepData[] = surveyState?.steps ?? [];

  const setSteps = (next: Templates.StepData[]) =>
    setCurrentSurvey({ ...surveyState, steps: next } as Utils.DBItem<Templates.SurveyTemplate>);

  const addStep = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nextId = steps.length > 0 ? Math.max(...steps.map((step: any) => step.id)) + 1 : 1;
    setSteps([...steps, { id: nextId, question: '', type: newType } as Templates.StepData]);
    // Open the new step straight away - it has nothing in it yet.
    setEditingId(nextId);
  };

  const onDragEnd = ({ destination, source }: DropResult) => {
    if (!destination || destination.index === source.index) return;
    const reordered = [...steps];
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);
    setSteps(reordered);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editingIndex = steps.findIndex((step: any) => step.id === editingId);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Editor = editingIndex >= 0 ? STEP_EDITORS[(steps[editingIndex] as any).type] : undefined;

  return (
    <Box className="SingleSurveyContent-root" sx={styles.root}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable-list">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
          {(provided: any) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: '4px' }}
            >
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {steps.map((step: any, i: number) => (
                <StepRow key={step.id} survey={surveyState!} index={i} onEdit={() => setEditingId(step.id)} />
              ))}
              {!steps.length && (
                <Typography variant="body2" sx={{ color: 'text.secondary', padding: '16px 0' }}>
                  {t('editor.hint.noStepsYet')}
                </Typography>
              )}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      <Box sx={{ flexShrink: 0, paddingTop: '12px' }}>
        <Divider sx={{ marginBottom: '12px' }} />
        <Typography variant="body2" fontWeight={600}>
          {t('editor.newStep.heading')}
        </Typography>
        <Box>
          <StepSelect label={t('editor.newStep.type')} value={newType} onChange={setNewType}>
            {STEP_GROUPS.flatMap((group) => [
              <ListSubheader key={group} sx={{ lineHeight: '32px', color: STEP_GROUP_COLOR[group] }}>
                {t(STEP_GROUP_LABEL_KEY[group])}
              </ListSubheader>,
              ...stepTypesByGroup(group).map((meta) => {
                const Icon = meta.icon;
                return (
                  <MenuItem key={meta.type} value={meta.type} sx={{ gap: 1 }}>
                    <Icon fontSize="small" sx={{ color: STEP_GROUP_COLOR[group] }} />
                    {t(meta.labelKey)}
                  </MenuItem>
                );
              }),
            ])}
          </StepSelect>
          {/* Same treatment as the add-row buttons inside the step editors. */}
          <AddRowButton label={t('editor.action.addStep')} onClick={addStep} />
        </Box>
      </Box>

      {/*
        Mounted only while open, and keyed by step id, so each open starts from
        the persisted step - which is what makes Cancel genuinely discard.
      */}
      {Editor && (
        <Editor key={editingId!} survey={surveyState!} index={editingIndex} onClose={() => setEditingId(null)} />
      )}
    </Box>
  );
};

export default SurveySingle;
